import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TagInput } from "@/components/TagInput";
import { EntitesCatalogue } from "@/components/EntitesCatalogue";

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export default function CompanySettings() {
  const { user } = useAuth();
  const canEdit = ["admin", "responsable_commercial"].includes(user?.role);
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/company").then(({ data }) => setC(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const setList = (k) => (v) => setC({ ...c, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { id, created_at, updated_at, ...payload } = c;
      const { data } = await api.put("/company", payload);
      setC(data);
      toast.success("Fiche société enregistrée");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!c) return <p className="text-sm text-muted-foreground">Chargement…</p>;

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="company-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold sm:text-4xl">Ma société</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {canEdit ? "Ces informations alimenteront la personnalisation des messages." : "Lecture seule — votre rôle ne permet pas la modification."}
          </p>
        </div>
        {canEdit && (
          <Button onClick={save} disabled={saving} data-testid="company-save-button">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        )}
      </div>

      <Tabs defaultValue="identite">
        <TabsList className="flex w-full flex-wrap justify-start h-auto">
          <TabsTrigger value="identite" data-testid="tab-identite">Identité</TabsTrigger>
          <TabsTrigger value="commercial" data-testid="tab-commercial">Commercial & signature</TabsTrigger>
          <TabsTrigger value="offre" data-testid="tab-offre">Services & arguments</TabsTrigger>
          <TabsTrigger value="exclusions" data-testid="tab-exclusions">Exclusions</TabsTrigger>
          <TabsTrigger value="catalogue" data-testid="tab-catalogue">Catalogue sociétés</TabsTrigger>
        </TabsList>

        <TabsContent value="catalogue">
          <EntitesCatalogue canEdit={["admin", "responsable_commercial", "commercial"].includes(user?.role)} />
        </TabsContent>

        <TabsContent value="identite">
          <Card><CardContent className="space-y-4 p-5">
            <fieldset disabled={!canEdit} className="space-y-4">
              <Field label="Raison sociale"><Input value={c.raison_sociale || ""} onChange={set("raison_sociale")} data-testid="company-name-input" /></Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Site internet"><Input value={c.site_internet || ""} onChange={set("site_internet")} placeholder="https://…" data-testid="company-website-input" /></Field>
                <Field label="URL du logo"><Input value={c.logo_url || ""} onChange={set("logo_url")} placeholder="https://…" data-testid="company-logo-input" /></Field>
              </div>
              {c.logo_url && <img src={c.logo_url} alt="Logo" className="h-16 rounded border object-contain p-1" />}
              <Field label="Description de l'activité"><Textarea rows={4} value={c.description || ""} onChange={set("description")} data-testid="company-description-input" /></Field>
              <Field label="Zones géographiques d'intervention"><TagInput value={c.zones_intervention} onChange={setList("zones_intervention")} placeholder="Ex : Lyon, Rhône (69)…" testId="company-zones-input" /></Field>
            </fieldset>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="commercial">
          <Card><CardContent className="space-y-4 p-5">
            <fieldset disabled={!canEdit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Nom du commercial"><Input value={c.commercial_nom || ""} onChange={set("commercial_nom")} data-testid="company-sales-name-input" /></Field>
                <Field label="E-mail du commercial"><Input value={c.commercial_email || ""} onChange={set("commercial_email")} data-testid="company-sales-email-input" /></Field>
                <Field label="Téléphone du commercial"><Input value={c.commercial_telephone || ""} onChange={set("commercial_telephone")} data-testid="company-sales-phone-input" /></Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Adresse d'expédition des e-mails"><Input value={c.adresse_expedition || ""} onChange={set("adresse_expedition")} placeholder="contact@…" data-testid="company-sender-input" /></Field>
                <Field label="Lien de prise de rendez-vous"><Input value={c.lien_rdv || ""} onChange={set("lien_rdv")} placeholder="https://calendly.com/…" data-testid="company-booking-input" /></Field>
              </div>
              <Field label="Signature e-mail"><Textarea rows={5} value={c.signature_email || ""} onChange={set("signature_email")} data-testid="company-signature-input" /></Field>
            </fieldset>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="offre">
          <Card><CardContent className="space-y-4 p-5">
            <fieldset disabled={!canEdit} className="space-y-4">
              <Field label="Services proposés"><TagInput value={c.services} onChange={setList("services")} testId="company-services-input" /></Field>
              <Field label="Arguments commerciaux"><TagInput value={c.arguments_commerciaux} onChange={setList("arguments_commerciaux")} testId="company-arguments-input" /></Field>
              <Field label="Avantages concurrentiels"><TagInput value={c.avantages_concurrentiels} onChange={setList("avantages_concurrentiels")} testId="company-advantages-input" /></Field>
              <Field label="Références clients"><TagInput value={c.references_clients} onChange={setList("references_clients")} testId="company-references-input" /></Field>
              <Field label="Tarifs indicatifs"><Textarea rows={3} value={c.tarifs_indicatifs || ""} onChange={set("tarifs_indicatifs")} data-testid="company-pricing-input" /></Field>
              <Field label="Questions fréquentes"><Textarea rows={4} value={c.questions_frequentes || ""} onChange={set("questions_frequentes")} data-testid="company-faq-input" /></Field>
              <Field label="Objections commerciales et réponses"><Textarea rows={4} value={c.objections_reponses || ""} onChange={set("objections_reponses")} data-testid="company-objections-input" /></Field>
            </fieldset>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="exclusions">
          <Card><CardContent className="space-y-4 p-5">
            <fieldset disabled={!canEdit} className="space-y-4">
              <Field label="Exclusions générales (entreprises, types de structures…)">
                <TagInput value={c.exclusions_generales} onChange={setList("exclusions_generales")} testId="company-exclusions-input" />
              </Field>
              <Field label="Domaines internet exclus">
                <TagInput value={c.domaines_exclus} onChange={setList("domaines_exclus")} placeholder="Ex : concurrent.fr" testId="company-domains-input" />
              </Field>
              <p className="text-xs text-muted-foreground">Ces exclusions s'appliqueront à toutes les campagnes et bloqueront la réimportation (Phase 3).</p>
            </fieldset>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
